import { SupportedAgent, SupportedModel } from "@src/gql/graphql";
import { tool } from "ai";
import { z } from "zod";
import {
	type AgentSkillAvailability,
	capSkillBody,
	getAgentSkill,
	listAgentSkillNames,
	rememberLoadedAgentSkill,
} from "./agent-skills";
import {
	generateImageInChatInputSchema,
	generateImageInChatModelOutput,
	isGenerateImageInChatOutput,
} from "./generate-image-in-chat";
import type { BoardAgentActions } from "./hooks/board-agent-context";
import {
	IMAGE_MODEL_SHORTLIST_SIZE,
	type ImageGenerationModelInfo,
	pickImageModelShortlist,
} from "./image-generation-models";
import {
	assertGenerationAuthorized,
	createPresentOptionsOutput,
	getPendingChoice,
} from "./present-options";
import {
	createArrowInputSchema,
	updateArrowInputSchema,
} from "./schemas/arrow-shape";
import { boardPlanInputSchema } from "./schemas/board-plan";
import { cardIdSchema, shapeIdSchema } from "./schemas/card-id";
import {
	agentDrawColorSchema,
	agentDrawFillSchema,
	agentLineSplineSchema,
	agentPointsSchema,
	agentStrokeDashSchema,
	agentStrokeSizeSchema,
} from "./schemas/draw-shape";
import {
	BOARD_GEO_SHAPE_TYPES,
	agentGeoAlignSchema,
	agentGeoColorSchema,
	agentGeoFillSchema,
	agentGeoFontSchema,
	agentGeoTypeSchema,
	agentGeoVerticalAlignSchema,
} from "./schemas/geo-shape";
import { presentOptionsInputSchema } from "./schemas/present-options";
import type { AgentChatMessage } from "./types";

const shapeIdsSchema = z
	.array(shapeIdSchema)
	.min(1)
	.describe("Exact shapeIds from listShapesOnPage or listShapesInViewport");

const alignOperationSchema = z.enum([
	"left",
	"center-horizontal",
	"right",
	"top",
	"center-vertical",
	"bottom",
]);

/** Shared geo style props for create + update. */
const geoStyleInputFields = {
	fill: agentGeoFillSchema.optional(),
	dash: agentStrokeDashSchema.optional(),
	size: agentStrokeSizeSchema.optional(),
	font: agentGeoFontSchema.optional(),
	align: agentGeoAlignSchema.optional(),
	verticalAlign: agentGeoVerticalAlignSchema.optional(),
	labelColor: agentGeoColorSchema
		.optional()
		.describe("Label text color (defaults to shape color)"),
};

/**
 * Shared across presentOptions / generateImageInChat for one turn.
 * Reset at the start of each user send (see use-agent-chat).
 */
export type ChatToolsSelectionGate = {
	presentOptionsCalled: boolean;
};

export type CreateChatToolsContext = {
	getImageModels: () => ImageGenerationModelInfo[];
	getMessages?: () => AgentChatMessage[];
	selectionGate?: ChatToolsSelectionGate;
	skillAvailability?: AgentSkillAvailability;
};

function skillNameSchema(availability: AgentSkillAvailability) {
	const [first, ...rest] = listAgentSkillNames(availability);
	if (!first) {
		return z.string().describe("No skills are available on this surface");
	}

	return z
		.enum([first, ...rest])
		.describe("Catalog skill name from the system prompt");
}

export function createChatTools(context?: CreateChatToolsContext) {
	const getImageModels = context?.getImageModels ?? (() => []);
	const getMessages = context?.getMessages ?? (() => []);
	const selectionGate = context?.selectionGate;
	const skillAvailability = context?.skillAvailability ?? {};

	return {
		presentOptions: tool({
			description:
				"Present selectable options for the user. Use when they must pick one option before you continue. After calling, wait for their selection — do not keep writing in the same turn.",
			inputSchema: presentOptionsInputSchema,
			execute: async ({ prompt, options }) => {
				if (selectionGate) {
					selectionGate.presentOptionsCalled = true;
				}
				return createPresentOptionsOutput(prompt, options);
			},
		}),
		listImageModels: tool({
			description:
				"List generation-eligible Image Maker models for in-chat image generation. Returns a page of models (default 3) plus suggestedPresentOptions for presentOptions (gateway modelIds, optional compare-all-3, optional show-more-models). Pass offset from a prior nextOffset when the user chooses show-more-models. Do not use for board/preprint generation.",
			inputSchema: z.object({
				offset: z
					.number()
					.int()
					.min(0)
					.optional()
					.describe(
						"Start index into the eligible model list (from prior nextOffset)",
					),
			}),
			execute: async ({ offset }) => {
				const models = getImageModels();
				return pickImageModelShortlist(models, {
					offset: offset ?? 0,
					limit: IMAGE_MODEL_SHORTLIST_SIZE,
				});
			},
		}),
		generateImageInChat: tool({
			description:
				"Generate image(s) into this chat via the LLM gateway (not the board). Pass prompt plus 1–3 gateway modelId strings from listImageModels / suggestedPresentOptions. For compare-all-3, pass the same shortlist modelIds just offered. Runs models in parallel; returns per-model ok/error. Only use when the user wants an image in chat — not when they ask to generate on the board/canvas/prompt card. Never call in the same turn as presentOptions — wait for the user's selection.",
			inputSchema: generateImageInChatInputSchema,
			execute: async ({ prompt, modelIds }, { abortSignal }) => {
				if (
					selectionGate?.presentOptionsCalled ||
					Boolean(getPendingChoice(getMessages()))
				) {
					throw new Error(
						"Cannot generate images while awaiting a model selection. Wait for the user to choose via presentOptions.",
					);
				}

				const catalog = getImageModels();
				const byModelId = new Map(
					catalog.map((model) => [model.modelId, model]),
				);
				const allowedIds: string[] = [];
				const deniedResults: Array<{
					modelId: string;
					status: "error";
					errorMessage: string;
					displayName?: string;
				}> = [];

				for (const modelId of modelIds) {
					if (byModelId.has(modelId)) {
						allowedIds.push(modelId);
					} else {
						deniedResults.push({
							modelId,
							status: "error",
							errorMessage: `Unknown or ineligible image model: ${modelId}`,
						});
					}
				}

				if (allowedIds.length === 0) {
					return { prompt, results: deniedResults };
				}

				const { runGenerateImageInChat } = await import(
					"./run-generate-image-in-chat"
				);
				// Full payload for the UI stream; model sees summary only
				// via toModelOutput (avoids base64 in the next completions call).
				const generated = await runGenerateImageInChat(
					{ prompt, modelIds: allowedIds },
					{ abortSignal },
				);

				const labeled = generated.results.map((result) => ({
					...result,
					displayName: byModelId.get(result.modelId)?.displayName,
				}));

				if (deniedResults.length === 0) {
					return { prompt: generated.prompt, results: labeled };
				}

				const byId = new Map(labeled.map((result) => [result.modelId, result]));
				return {
					prompt: generated.prompt,
					results: modelIds.map(
						(modelId) =>
							byId.get(modelId) ??
							deniedResults.find((row) => row.modelId === modelId) ?? {
								modelId,
								status: "error" as const,
								errorMessage: `Unknown or ineligible image model: ${modelId}`,
							},
					),
				};
			},
			toModelOutput: ({ output }) => {
				if (!isGenerateImageInChatOutput(output)) {
					return { type: "json", value: null };
				}
				return generateImageInChatModelOutput(output);
			},
		}),
		loadSkill: tool({
			description:
				"WHEN TO USE: the task matches a skill listed in the system prompt catalog. WHEN NOT TO USE: ordinary chat that does not need a listed skill. DO NOT USE FOR: a name that is not in the catalog.",
			inputSchema: z.object({
				name: skillNameSchema(skillAvailability),
			}),
			execute: async ({ name }) => {
				const skill = getAgentSkill(name, skillAvailability);
				if (!skill) {
					return {
						ok: false as const,
						error: `Unknown or unavailable skill: ${name}`,
					};
				}

				rememberLoadedAgentSkill(skill.name);
				return {
					ok: true as const,
					name: skill.name,
					body: capSkillBody(skill.body),
				};
			},
		}),
	};
}

type BoardAgentToolContext = {
	actions: BoardAgentActions;
	getMessages: () => AgentChatMessage[];
};

export function createBoardTools({
	actions,
	getMessages,
}: BoardAgentToolContext) {
	return {
		getBoardSnapshot: tool({
			description:
				"Get the latest board snapshot including board name, editability, " +
				"visible cards, selected cards, viewport, and shape counts. Call this " +
				"when you need current canvas state before answering about the board " +
				"or choosing mutations — do not assume board state.",
			inputSchema: z.object({}),
			execute: async () => actions.getSnapshot(),
		}),
		listVisibleCards: tool({
			description:
				"List visible cards with summaries, cardId, and shapeId. Use shapeId " +
				"as fromShapeId/toShapeId when createArrow connects cards for context.",
			inputSchema: z.object({}),
			execute: async () => actions.listVisibleCards(),
		}),
		listSelectedCards: tool({
			description: "List selected cards with readable content summaries.",
			inputSchema: z.object({}),
			execute: async () => actions.listSelectedCards(),
		}),
		getCardContent: tool({
			description:
				"Get the full text content of a specific card by its card id.",
			inputSchema: z.object({
				cardId: cardIdSchema,
			}),
			strict: true,
			execute: async ({ cardId }) => actions.getCardContent(cardId),
		}),
		openCard: tool({
			description:
				"Open a specific card by its card id without changing the camera.",
			inputSchema: z.object({
				cardId: cardIdSchema,
			}),
			strict: true,
			execute: async ({ cardId }) => actions.openCard(cardId),
		}),
		navigateToCard: tool({
			description:
				"Navigate the board viewport to a specific card by its card id.",
			inputSchema: z.object({
				cardId: cardIdSchema,
			}),
			strict: true,
			execute: async ({ cardId }) => actions.navigateToCard(cardId),
		}),
		createNoteCard: tool({
			description:
				"Create a new note card in non-overlapping space near the viewport. " +
				"Safe to create several in one turn — each call reserves space so cards " +
				"do not stack. Never stack cards unless the user asks to stack/overlay.",
			inputSchema: z.object({
				title: z.string().min(1).describe("Note card title"),
				content: z.string().optional().describe("Optional note body text"),
			}),
			execute: async ({ title, content }) =>
				actions.createNoteCard({
					title,
					content,
				}),
		}),
		listPromptCards: tool({
			description: "List all prompt cards on the current board.",
			inputSchema: z.object({}),
			execute: async () => actions.listPromptCards(),
		}),
		listPreprintCards: tool({
			description: "List all preprint (AI output) cards on the current board.",
			inputSchema: z.object({}),
			execute: async () => actions.listPreprintCards(),
		}),
		createPromptCard: tool({
			description:
				"Create a new prompt card in non-overlapping space near the viewport. " +
				"Safe to create several in one turn — placements do not stack unless " +
				"the user asks to stack/overlay. To attach existing notes as context, " +
				"follow with createArrow (note shapeId → this prompt shapeId).",
			inputSchema: z.object({
				prompt: z.string().min(1).describe("Prompt text for the new card"),
			}),
			execute: async ({ prompt }) => actions.createPromptCard({ prompt }),
		}),
		generateFromPromptCard: tool({
			description:
				"Create a connected preprint card and start generation asynchronously with one model. Use only when the user explicitly wants board/prompt-card generation (not for ordinary chat answers). Prefer omitting model to use the board default, or pass a model the user already named. Returns when the preprint exists (started=true), not when AI output is ready. Only use models returned as generation-eligible from listAvailableAgentsAndModels. Fails when the weekly AI limit is reached.",
			inputSchema: z.object({
				cardId: cardIdSchema,
				promptText: z.string().min(1).describe("Prompt text to generate from"),
				model: z
					.nativeEnum(SupportedModel)
					.optional()
					.describe("SupportedModel enum value chosen by the user"),
				agent: z
					.nativeEnum(SupportedAgent)
					.optional()
					.describe("Agent to use for generation"),
				webSearch: z
					.boolean()
					.optional()
					.describe("Whether to enable web search for this generation"),
			}),
			execute: async ({ cardId, promptText, model, agent, webSearch }) => {
				assertGenerationAuthorized(getMessages(), {
					model,
					allModels: false,
				});
				return actions.generateFromPromptCard({
					cardId,
					promptText,
					model,
					agent,
					webSearch,
				});
			},
		}),
		generateFromPromptCardAllModels: tool({
			description:
				"Create preprint cards and start generation asynchronously with every generation-eligible model for the chosen agent. Use only when the user explicitly wants all-models board generation. Confirm via presentOptions with option id `all-models` first. Returns when preprints are created (started=true), not when AI output is ready. Fails when the weekly AI limit is reached or no models are generation-eligible.",
			inputSchema: z.object({
				cardId: cardIdSchema,
				promptText: z.string().min(1).describe("Prompt text to generate from"),
				agent: z.nativeEnum(SupportedAgent).optional(),
				webSearch: z.boolean().optional(),
			}),
			execute: async ({ cardId, promptText, agent, webSearch }) => {
				assertGenerationAuthorized(getMessages(), {
					allModels: true,
				});
				return actions.generateFromPromptCardAllModels({
					cardId,
					promptText,
					agent,
					webSearch,
				});
			},
		}),
		listAvailableAgentsAndModels: tool({
			description:
				"List agents and models as shown in the board picker, including locked ones. Each model has generationEligible, tierAvailable, and lockReason. generationEligible means the user can generate now; locked models still appear in the UI — report the lock reason, do not call them unavailable.",
			inputSchema: z.object({
				agent: z.nativeEnum(SupportedAgent).optional(),
				webSearch: z.boolean().optional(),
			}),
			execute: async ({ agent, webSearch }) =>
				actions.listAvailableAgentsAndModels({
					agent,
					webSearch,
				}),
		}),
		fitBoardToContent: tool({
			description:
				"Zoom and pan the board viewport to fit all content on the page.",
			inputSchema: z.object({}),
			execute: async () => actions.fitBoardToContent(),
		}),
		openBoardSearch: tool({
			description: "Open the board quick-search panel.",
			inputSchema: z.object({}),
			execute: async () => actions.openBoardSearch(),
		}),
		listShapesInViewport: tool({
			description:
				"List shapes in the current viewport with bounds, labels, geo types, colors, and arrow bindings in summaries.",
			inputSchema: z.object({}),
			execute: async () => actions.listShapesInViewport(),
		}),
		listShapesOnPage: tool({
			description:
				"List all shapes on the current page with enriched summaries. Prefer over listShapesInViewport after drawing or before review.",
			inputSchema: z.object({
				type: z
					.string()
					.optional()
					.describe("Optional shape type filter, e.g. geo or arrow"),
			}),
			execute: async ({ type }) => actions.listShapesOnPage({ type }),
		}),
		listSelectedShapes: tool({
			description:
				"List selected shapes with bounds, type labels, and one-line summaries.",
			inputSchema: z.object({}),
			execute: async () => actions.listSelectedShapes(),
		}),
		getArrowConnections: tool({
			description:
				"List all arrows on the page with resolved fromShapeId and toShapeId bindings.",
			inputSchema: z.object({}),
			execute: async () => actions.getArrowConnections(),
		}),
		inspectDiagram: tool({
			description:
				"Review the page: shapes, arrow bindings, unbound arrows. " +
				"overlapCount = box–box collisions only (geo/note/text/frame/cards/" +
				"card-sections) — MUST redistribute with large stackGap moves if > 0. " +
				"overlappingArrowCount = arrow–arrow collisions — curve with opposite bend. " +
				"Also sketch metrics detachedDrawPartCount / sparseDrawStrokeCount. " +
				"Arrow AABBs touching connected geos are ignored.",
			inputSchema: z.object({}),
			execute: async () => actions.inspectDiagram(),
		}),
		getShapeContent: tool({
			description:
				"Get readable content from any shape by shape id. Use getCardContent for cards when you have a card id.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
			}),
			strict: true,
			execute: async ({ shapeId }) => actions.getShapeContent(shapeId),
		}),
		navigateToShape: tool({
			description: "Navigate the board viewport to a shape by shape id.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
			}),
			strict: true,
			execute: async ({ shapeId }) => actions.navigateToShape(shapeId),
		}),
		listCustomShapes: tool({
			description:
				"List Illumi custom shapes (cards, card sections, custom images) on the board.",
			inputSchema: z.object({}),
			execute: async () => actions.listCustomShapes(),
		}),
		getCustomShapeInfo: tool({
			description:
				"Get structured metadata for an Illumi custom shape by shape id.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
			}),
			strict: true,
			execute: async ({ shapeId }) => actions.getCustomShapeInfo(shapeId),
		}),
		findEmptySpace: tool({
			description:
				"Find a non-overlapping position for a rectangle of the given size. Call before creating cards or shapes when placement matters.",
			inputSchema: z.object({
				width: z.number().positive(),
				height: z.number().positive(),
				nearViewport: z.boolean().optional(),
				padding: z.number().positive().optional(),
			}),
			execute: async ({ width, height, nearViewport, padding }) =>
				actions.findEmptySpace({
					width,
					height,
					nearViewport,
					padding,
				}),
		}),
		createGeoShape: tool({
			description: `Create a geo shape (${BOARD_GEO_SHAPE_TYPES.join(", ")}). For diagrams: put labels in text; style fill/dash/size/font/align/verticalAlign/labelColor; AUTO-GROW so set w/h large enough (single-line h≥70, two-line h≥110); stack with ≥120px clear gap. For illustrations/sketches: primary building block — ellipses/ovals for heads & bodies, triangles for ears/roofs, rectangles for torsos/legs; use solid/semi fill; place parts so they TOUCH. Prefer many geos over createDrawShape.`,
			inputSchema: z.object({
				geo: agentGeoTypeSchema.describe("Geo shape type to create"),
				x: z.number(),
				y: z.number(),
				w: z.number().positive(),
				h: z.number().positive(),
				text: z
					.string()
					.optional()
					.describe("Label rendered inside the geo shape"),
				color: agentGeoColorSchema.optional(),
				...geoStyleInputFields,
			}),
			execute: async (input) => actions.createGeoShape(input),
		}),
		createTextShape: tool({
			description:
				"Create standalone floating text at x, y. Use only when text is not tied to another shape — prefer createGeoShape text, createNoteShape text, createArrow label, or createFrameShape name for in-shape labels.",
			inputSchema: z.object({
				x: z.number(),
				y: z.number(),
				text: z.string().min(1),
			}),
			execute: async ({ x, y, text }) =>
				actions.createTextShape({ x, y, text }),
		}),
		createArrow: tool({
			description:
				"Connect shapes with a bound arrow (diagrams OR Illumi cards). " +
				"Use exact shapeIds (shape:…), not card entity ids. For card context: " +
				"fromShapeId = note/preprint/source card, toShapeId = prompt card — " +
				"context flows into the prompt along the arrow. Label optional. " +
				"Curve overlapping routes: kind=arc with bend (±40..120, opposite signs " +
				"for parallel arrows). kind=elbow for orthogonal routing (elbowMidPoint 0–1).",
			inputSchema: createArrowInputSchema,
			execute: async (input) => actions.createArrow(input),
		}),
		createDrawShape: tool({
			description:
				"LAST RESORT freehand pencil/pen stroke. For animals/icons/scenes use " +
				"createGeoShape + createLineShape instead — freehand usually looks broken. " +
				"Only use when the user explicitly wants pencil doodles or underlines. " +
				"If used: dense page-space points (≥8 on curves), joints must touch, " +
				"then exportViewportImage and repair.",
			inputSchema: z.object({
				points: agentPointsSchema,
				color: agentDrawColorSchema.optional(),
				size: agentStrokeSizeSchema.optional(),
				dash: agentStrokeDashSchema.optional(),
				isClosed: z
					.boolean()
					.optional()
					.describe("Close the path into a filled silhouette when true"),
				fill: agentDrawFillSchema.optional(),
			}),
			execute: async (input) => actions.createDrawShape(input),
		}),
		createHighlightShape: tool({
			description:
				"Highlighter marker stroke (semi-transparent) through page-space points. " +
				"Use to emphasize regions or underline — not for diagrams or picture sketches. " +
				"Dense points (~8–20px). Prefer size l/xl for broad marks.",
			inputSchema: z.object({
				points: agentPointsSchema,
				color: agentDrawColorSchema.optional(),
				size: agentStrokeSizeSchema.optional(),
			}),
			execute: async (input) => actions.createHighlightShape(input),
		}),
		createLineShape: tool({
			description:
				"Straight or cubic line through page-space points. Use for sketch limbs " +
				"(whiskers, tails, legs, antennae), polylines, and diagram edges when " +
				"createArrow is not needed. Prefer over createDrawShape for illustrations.",
			inputSchema: z.object({
				points: agentPointsSchema,
				color: agentDrawColorSchema.optional(),
				size: agentStrokeSizeSchema.optional(),
				spline: agentLineSplineSchema.optional(),
			}),
			execute: async ({ points, color, size, spline }) =>
				actions.createLineShape({ points, color, size, spline }),
		}),
		createNoteShape: tool({
			description:
				"Create a sticky note at x, y with text inside the note shape.",
			inputSchema: z.object({
				x: z.number(),
				y: z.number(),
				text: z
					.string()
					.min(1)
					.describe("Note body rendered inside the sticky note"),
				color: agentDrawColorSchema.optional(),
			}),
			execute: async ({ x, y, text, color }) =>
				actions.createNoteShape({ x, y, text, color }),
		}),
		createFrameShape: tool({
			description:
				"Create a frame region at x, y with width w and height h. Use name for the frame title — not a separate text shape.",
			inputSchema: z.object({
				x: z.number(),
				y: z.number(),
				w: z.number().positive(),
				h: z.number().positive(),
				name: z
					.string()
					.optional()
					.describe("Frame title rendered on the frame"),
				color: agentDrawColorSchema.optional(),
			}),
			execute: async ({ x, y, w, h, name, color }) =>
				actions.createFrameShape({ x, y, w, h, name, color }),
		}),
		updateGeoShape: tool({
			description:
				"Update an existing geo shape (position, size, label, color, geo type, " +
				"fill/dash/size/font/align). Prefer this over delete+recreate when adjusting.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				w: z.number().positive().optional(),
				h: z.number().positive().optional(),
				text: z.string().optional(),
				color: agentGeoColorSchema.optional(),
				geo: agentGeoTypeSchema.optional(),
				...geoStyleInputFields,
			}),
			execute: async (input) => actions.updateGeoShape(input),
		}),
		updateArrow: tool({
			description:
				"Update an arrow label, bindings, and/or curve (kind/bend/elbowMidPoint). " +
				"If inspectDiagram shows overlapping arrows, set kind=arc and bend with " +
				"opposite signs (e.g. +80 / -80) — do not recreate.",
			inputSchema: updateArrowInputSchema,
			execute: async (input) => actions.updateArrow(input),
		}),
		updateTextShape: tool({
			description: "Update floating text position or content.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				text: z.string().optional(),
			}),
			execute: async (input) => actions.updateTextShape(input),
		}),
		updateNoteShape: tool({
			description: "Update sticky note position, text, or color.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				text: z.string().optional(),
				color: agentDrawColorSchema.optional(),
			}),
			execute: async (input) => actions.updateNoteShape(input),
		}),
		updateFrameShape: tool({
			description: "Update frame bounds, title, or color.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				w: z.number().positive().optional(),
				h: z.number().positive().optional(),
				name: z.string().optional(),
				color: agentDrawColorSchema.optional(),
			}),
			execute: async (input) => actions.updateFrameShape(input),
		}),
		updateDrawShape: tool({
			description:
				"Update an existing draw stroke (points, style, fill). Prefer over " +
				"delete+recreate when adjusting a sketch.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				points: agentPointsSchema.optional(),
				color: agentDrawColorSchema.optional(),
				size: agentStrokeSizeSchema.optional(),
				dash: agentStrokeDashSchema.optional(),
				isClosed: z.boolean().optional(),
				fill: agentDrawFillSchema.optional(),
			}),
			execute: async (input) => actions.updateDrawShape(input),
		}),
		updateLineShape: tool({
			description: "Update line points or style.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				points: agentPointsSchema.optional(),
				color: agentDrawColorSchema.optional(),
				size: agentStrokeSizeSchema.optional(),
				spline: agentLineSplineSchema.optional(),
			}),
			execute: async (input) => actions.updateLineShape(input),
		}),
		updateHighlightShape: tool({
			description:
				"Update an existing highlight stroke. Prefer over delete+recreate.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				points: agentPointsSchema.optional(),
				color: agentDrawColorSchema.optional(),
				size: agentStrokeSizeSchema.optional(),
			}),
			execute: async (input) => actions.updateHighlightShape(input),
		}),
		updateImageShape: tool({
			description: "Update Illumi image shape bounds or URL.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number().optional(),
				y: z.number().optional(),
				w: z.number().positive().optional(),
				h: z.number().positive().optional(),
				url: z.string().url().optional(),
			}),
			execute: async (input) => actions.updateImageShape(input),
		}),
		resizeShapes: tool({
			description:
				"Resize canvas shapes by scale factors or width/height deltas.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				scaleX: z.number().positive().optional(),
				scaleY: z.number().positive().optional(),
				dw: z.number().optional(),
				dh: z.number().optional(),
			}),
			execute: async (input) => actions.resizeShapes(input),
		}),
		rotateShapes: tool({
			description: "Rotate canvas shapes by degrees or radians.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				degrees: z.number().optional(),
				radians: z.number().optional(),
			}),
			execute: async (input) => actions.rotateShapes(input),
		}),
		flipShapes: tool({
			description: "Flip canvas shapes horizontally or vertically.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				axis: z.enum(["horizontal", "vertical"]),
			}),
			execute: async (input) => actions.flipShapes(input),
		}),
		nudgeShapes: tool({
			description: "Nudge canvas shapes by dx and dy page units.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				dx: z.number(),
				dy: z.number(),
			}),
			execute: async (input) => actions.nudgeShapes(input),
		}),
		lockShapes: tool({
			description: "Lock canvas shapes to prevent edits.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
			}),
			execute: async ({ shapeIds }) => actions.lockShapes({ shapeIds }),
		}),
		unlockShapes: tool({
			description: "Unlock previously locked canvas shapes.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
			}),
			execute: async ({ shapeIds }) => actions.unlockShapes({ shapeIds }),
		}),
		setCamera: tool({
			description: "Set board camera position and zoom (z).",
			inputSchema: z.object({
				x: z.number(),
				y: z.number(),
				z: z.number().positive(),
			}),
			execute: async (input) => actions.setCamera(input),
		}),
		getCamera: tool({
			description: "Retrieve the current board camera position and zoom (z).",
			inputSchema: z.object({}),
			execute: async () => actions.getCamera(),
		}),
		selectShapes: tool({
			description: "Select shapes on the board by shape id.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
			}),
			execute: async ({ shapeIds }) => actions.selectShapes({ shapeIds }),
		}),
		moveCard: tool({
			description: "Move an Illumi card to a new x, y position.",
			inputSchema: z.object({
				cardId: cardIdSchema,
				x: z.number(),
				y: z.number(),
			}),
			execute: async (input) => actions.moveCard(input),
		}),
		resizeCard: tool({
			description: "Resize an Illumi card by updating props w and/or h.",
			inputSchema: z.object({
				cardId: cardIdSchema,
				w: z.number().positive().optional(),
				h: z.number().positive().optional(),
			}),
			execute: async (input) => actions.resizeCard(input),
		}),
		updateCardContent: tool({
			description:
				"Replace note or prompt card body text (markdown). Provide title and/or content.",
			inputSchema: z.object({
				cardId: cardIdSchema,
				title: z.string().optional(),
				content: z.string().optional(),
			}),
			execute: async (input) => actions.updateCardContent(input),
		}),
		deleteCard: tool({
			description: "Delete an Illumi card by card id.",
			inputSchema: z.object({
				cardId: cardIdSchema,
			}),
			execute: async ({ cardId }) => actions.deleteCard({ cardId }),
		}),
		duplicateCard: tool({
			description:
				"Duplicate an Illumi card with a new card id and offset position.",
			inputSchema: z.object({
				cardId: cardIdSchema,
				offsetX: z.number().optional(),
				offsetY: z.number().optional(),
			}),
			execute: async (input) => actions.duplicateCard(input),
		}),
		lockCard: tool({
			description: "Lock an Illumi card to prevent edits.",
			inputSchema: z.object({
				cardId: cardIdSchema,
			}),
			execute: async ({ cardId }) => actions.lockCard({ cardId }),
		}),
		unlockCard: tool({
			description: "Unlock a locked Illumi card.",
			inputSchema: z.object({
				cardId: cardIdSchema,
			}),
			execute: async ({ cardId }) => actions.unlockCard({ cardId }),
		}),
		createCardSection: tool({
			description:
				"Create a card section around one or more cards by card id or shape id. " +
				"Do not place sections on top of existing cards/sections unless asked.",
			inputSchema: z.object({
				cardIds: z.array(cardIdSchema).optional(),
				shapeIds: z.array(shapeIdSchema).optional(),
				name: z.string().optional(),
				x: z.number().optional(),
				y: z.number().optional(),
				w: z.number().positive().optional(),
				h: z.number().positive().optional(),
			}),
			execute: async (input) => actions.createCardSection(input),
		}),
		moveShapesToCardSection: tool({
			description: "Move cards into an existing card section.",
			inputSchema: z.object({
				sectionId: shapeIdSchema,
				cardIds: z.array(cardIdSchema).optional(),
				shapeIds: z.array(shapeIdSchema).optional(),
			}),
			execute: async (input) => actions.moveShapesToCardSection(input),
		}),
		runBoardPlan: tool({
			description:
				"Execute two or more board mutations as one ordered plan (one undo). " +
				"New diagrams: put createGeoShape/createDrawShape/createHighlightShape " +
				"AND createArrow in the SAME plan; wire arrows with $0, $1, …. " +
				"Adjustments (align, move, resize, restyle, fix overlaps): use mutation ops " +
				"(alignShapes, moveShape, updateGeoShape, updateDrawShape, distributeShapes, " +
				"…) on EXISTING shapeIds — never delete everything and recreate. " +
				"Never invent shapeIds. Never call createArrow in the same LLM step as " +
				"runBoardPlan — either include arrows in the plan or wait for plan results.",
			inputSchema: boardPlanInputSchema,
			execute: async (input) => actions.runBoardPlan(input),
		}),
		moveShape: tool({
			description:
				"Move a native canvas shape to a new x, y. Cannot move Illumi cards.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
				x: z.number(),
				y: z.number(),
			}),
			strict: true,
			execute: async ({ shapeId, x, y }) =>
				actions.moveShape({ shapeId, x, y }),
		}),
		alignShapes: tool({
			description:
				"Align two or more shapes along an edge (left/right/top/bottom) or " +
				"centers. Align does NOT unstack overlapping cards — for a neat row " +
				"use distributeShapes first, then alignShapes with top or left. Never " +
				"use center-horizontal/center-vertical alone on stacked cards.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				operation: alignOperationSchema,
			}),
			execute: async ({ shapeIds, operation }) =>
				actions.alignShapes({ shapeIds, operation }),
		}),
		distributeShapes: tool({
			description:
				"Distribute three or more shapes evenly horizontally or vertically. " +
				"Use to unstack overlapping cards/sections into a row or column. Works " +
				"even when shapes share the same origin (spreads with gap).",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				operation: z.enum(["horizontal", "vertical"]),
			}),
			execute: async ({ shapeIds, operation }) =>
				actions.distributeShapes({ shapeIds, operation }),
		}),
		duplicateShapes: tool({
			description: "Duplicate canvas shapes with a small offset.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				offsetX: z.number().optional(),
				offsetY: z.number().optional(),
			}),
			execute: async ({ shapeIds, offsetX, offsetY }) =>
				actions.duplicateShapes({ shapeIds, offsetX, offsetY }),
		}),
		groupShapes: tool({
			description: "Group two or more canvas shapes.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
			}),
			execute: async ({ shapeIds }) => actions.groupShapes({ shapeIds }),
		}),
		ungroupShapes: tool({
			description: "Ungroup grouped canvas shapes.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
			}),
			execute: async ({ shapeIds }) => actions.ungroupShapes({ shapeIds }),
		}),
		moveShapesToFrame: tool({
			description: "Move canvas shapes into a frame by frame shape id.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				frameId: shapeIdSchema,
			}),
			execute: async ({ shapeIds, frameId }) =>
				actions.moveShapesToFrame({ shapeIds, frameId }),
		}),
		setShapeZOrder: tool({
			description: "Bring shapes to front or send them to back.",
			inputSchema: z.object({
				shapeIds: shapeIdsSchema,
				operation: z.enum(["front", "back"]),
			}),
			execute: async ({ shapeIds, operation }) =>
				actions.setShapeZOrder({ shapeIds, operation }),
		}),
		exportViewportImage: tool({
			description:
				"Export a tight PNG crop of SPECIFIC shapes for visual review (max ~896px " +
				"edge). ALWAYS pass shapeIds from the illustration / diagram you just made " +
				"(runBoardPlan.touchedShapeIds). Omitting shapeIds falls back to this turn’s " +
				"touched shapes, then selection — NEVER the full page. Required after " +
				"illustrations; if the crop looks wrong, fix shapes then re-export with ids.",
			inputSchema: z.object({
				shapeIds: z
					.array(shapeIdSchema)
					.optional()
					.describe(
						"Shape ids to crop tightly. Prefer runBoardPlan.touchedShapeIds.",
					),
			}),
			execute: async ({ shapeIds }) =>
				actions.exportViewportImage({ shapeIds }),
		}),
		getBoardDocumentSnapshot: tool({
			description:
				"Get the full board document snapshot JSON. Large payload — use sparingly.",
			inputSchema: z.object({}),
			execute: async () => actions.getBoardDocumentSnapshot(),
		}),
		createImageShape: tool({
			description:
				"Create an Illumi image shape from an http(s) URL at x, y with size w × h.",
			inputSchema: z.object({
				url: z.string().url(),
				x: z.number(),
				y: z.number(),
				w: z.number().positive(),
				h: z.number().positive(),
			}),
			execute: async ({ url, x, y, w, h }) =>
				actions.createImageShape({ url, x, y, w, h }),
		}),
		deleteShape: tool({
			description:
				"Delete a canvas shape by shape id (eraser). Use when fixing layout mistakes. Cannot delete Illumi cards or custom shapes.",
			inputSchema: z.object({
				shapeId: shapeIdSchema,
			}),
			strict: true,
			execute: async ({ shapeId }) => actions.deleteShape(shapeId),
		}),
	};
}
